import { useEffect, useState } from 'react';
import { apiClient } from '@/services/api';
import type { SyncedMenuGroup } from '@/types/api';
import { ColorPickerField } from '@/components/ui/ColorPickerField';
import { TranslationsEditor } from '@/components/ui/TranslationsEditor';

interface MenuGroupFormProps {
  formData: Partial<SyncedMenuGroup>;
  onChange: (data: Partial<SyncedMenuGroup>) => void;
  restaurantId: number;
}

interface SelectOption {
  id: number | string;
  name: string;
}

interface ShiftOption {
  id: number | string;
  // null when a mgmt-created shift hasn't been acknowledged by the POS yet.
  posId: number | null;
  name: string;
}

export function MenuGroupForm({ formData, onChange, restaurantId }: MenuGroupFormProps) {
  const [categories, setCategories] = useState<SelectOption[]>([]);
  const [cookingInstructions, setCookingInstructions] = useState<SelectOption[]>([]);
  const [kitchenDisplayOrders, setKitchenDisplayOrders] = useState<SelectOption[]>([]);
  const [accountSalesAreas, setAccountSalesAreas] = useState<SelectOption[]>([]);
  const [shortcutCategories, setShortcutCategories] = useState<SelectOption[]>([]);
  const [shifts, setShifts] = useState<ShiftOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    Promise.all([
      apiClient.getSyncedMenuCategories(restaurantId),
      apiClient.getSyncedCookingInstructions(restaurantId).catch((): any[] => []),
      apiClient.getSyncedKitchenDisplayOrders(restaurantId).catch((): any[] => []),
      apiClient.getSyncedAccountSalesAreas(restaurantId).catch((): any[] => []),
      apiClient.getSyncedShifts(restaurantId).catch((): any[] => []),
    ]).then(([cats, cis, kdos, asas, shiftsData]) => {
      setCategories(cats as any);
      setCookingInstructions(cis as any);
      setKitchenDisplayOrders(kdos as any);
      setAccountSalesAreas(asas as any);
      setShortcutCategories((cats as any[]).filter((c: any) => c.isMenuGroupShortcut));
      // Do NOT fall back to `s.id` (a mgmt-server PK) when `posShiftId` is null --
      // that would silently point shiftIds at an unrelated, real shift that
      // happens to share the same numeric id (F3).
      setShifts((shiftsData as any[]).map((s: any) => ({ id: s.id, posId: s.posShiftId ?? null, name: s.name })));
    }).finally(() => setLoading(false));
  }, [restaurantId]);

  const handleCookingInstructionToggle = (posId: number) => {
    const current = (formData as any).cookingInstructionIds || [];
    const updated = current.includes(posId)
      ? current.filter((id: number) => id !== posId)
      : [...current, posId];
    onChange({ ...formData, cookingInstructionIds: updated } as any);
  };

  const handleShiftToggle = (posId: number) => {
    const current = formData.shiftIds || [];
    const updated = current.includes(posId)
      ? current.filter((id: number) => id !== posId)
      : [...current, posId];
    onChange({ ...formData, shiftIds: updated });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input type="text" value={formData.name || ''} onChange={(e) => onChange({ ...formData, name: e.target.value })} className="input w-full" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea value={formData.description || ''} onChange={(e) => onChange({ ...formData, description: e.target.value })} className="input w-full" rows={2} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
          <select
            value={formData.categoryId ?? ''}
            onChange={(e) => onChange({ ...formData, categoryId: e.target.value ? parseInt(e.target.value) : undefined })}
            className="input w-full"
            disabled={loading}
          >
            <option value="">-- Select --</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Shortcut Category</label>
          <select
            value={(formData as any).shortcutCategoryId ?? ''}
            onChange={(e) => onChange({ ...formData, shortcutCategoryId: e.target.value ? parseInt(e.target.value) : null } as any)}
            className="input w-full"
            disabled={loading}
          >
            <option value="">-- None --</option>
            {shortcutCategories.map((cat) => {
              const posId = (cat as any).posCategoryId;
              return (
                <option key={cat.id} value={posId ?? ''} disabled={posId == null}>
                  {cat.name}{posId == null ? ' (unsynced)' : ''}
                </option>
              );
            })}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Kitchen Display</label>
          <select
            value={(formData as any).kitchenDisplayOrderId ?? ''}
            onChange={(e) => onChange({ ...formData, kitchenDisplayOrderId: e.target.value ? parseInt(e.target.value) : null } as any)}
            className="input w-full"
            disabled={loading}
          >
            <option value="">-- None --</option>
            {kitchenDisplayOrders.map((kdo) => {
              const posId = (kdo as any).posKitchenDisplayOrderId;
              return (
                <option key={kdo.id} value={posId ?? ''} disabled={posId == null}>
                  {kdo.name}{posId == null ? ' (unsynced)' : ''}
                </option>
              );
            })}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Account Sales Area</label>
          <select
            value={(formData as any).accountSalesAreaId ?? ''}
            onChange={(e) => onChange({ ...formData, accountSalesAreaId: e.target.value || null } as any)}
            className="input w-full"
            disabled={loading}
          >
            <option value="">-- None --</option>
            {accountSalesAreas.map((asa) => {
              // accountSalesAreaId is a String (POS uses GlobalIdGenerator, not a
              // numeric id, for this entity) -- no `asa.id` fallback at all, since
              // even a stringified mgmt PK would still be the wrong id space (F3).
              const posId = (asa as any).posAccountSalesAreaId;
              return (
                <option key={asa.id} value={posId ?? ''} disabled={posId == null}>
                  {asa.name}{posId == null ? ' (unsynced)' : ''}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {cookingInstructions.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cooking Instructions</label>
          <div className="border rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
            {cookingInstructions.map((ci) => {
              // No `ci.id` fallback -- that's a mgmt-server PK, a different id
              // space than posInstructionId, and substituting it would point
              // cookingInstructionIds at an unrelated real instruction (F3).
              const posId = (ci as any).posInstructionId ?? null;
              const unsynced = posId === null;
              const checked = posId !== null && ((formData as any).cookingInstructionIds || []).includes(posId);
              return (
                <div key={ci.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`ci-${ci.id}`}
                    checked={checked}
                    disabled={unsynced}
                    title={unsynced ? 'Not yet synced to the POS -- cannot be linked yet' : undefined}
                    onChange={() => posId !== null && handleCookingInstructionToggle(posId)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label
                    htmlFor={`ci-${ci.id}`}
                    className={`ml-2 text-sm ${unsynced ? 'text-gray-400 italic' : 'text-gray-700'}`}
                  >
                    {(ci as any).description || ci.name}{unsynced ? ' (unsynced)' : ''}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {shifts.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Shifts</label>
          <div className="border rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
            {shifts.map((shift) => {
              const unsynced = shift.posId === null;
              const checked = shift.posId !== null && (formData.shiftIds || []).includes(shift.posId);
              return (
                <div key={shift.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`mg-shift-${shift.id}`}
                    checked={checked}
                    disabled={unsynced}
                    title={unsynced ? 'Not yet synced to the POS -- cannot be linked yet' : undefined}
                    onChange={() => shift.posId !== null && handleShiftToggle(shift.posId)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label
                    htmlFor={`mg-shift-${shift.id}`}
                    className={`ml-2 text-sm ${unsynced ? 'text-gray-400 italic' : 'text-gray-700'}`}
                  >
                    {shift.name}{unsynced ? ' (unsynced)' : ''}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notification</label>
        <textarea value={formData.notification || ''} onChange={(e) => onChange({ ...formData, notification: e.target.value || undefined })} className="input w-full" rows={2} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center">
          <input type="checkbox" id="mg-isVisible" checked={formData.isVisible ?? true} onChange={(e) => onChange({ ...formData, isVisible: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          <label htmlFor="mg-isVisible" className="ml-2 text-sm text-gray-700">Visible</label>
        </div>
        <div className="flex items-center">
          <input type="checkbox" id="mg-isVisibleOnSite" checked={formData.isVisibleOnSite ?? false} onChange={(e) => onChange({ ...formData, isVisibleOnSite: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          <label htmlFor="mg-isVisibleOnSite" className="ml-2 text-sm text-gray-700">Visible on Site</label>
        </div>
        <div className="flex items-center">
          <input type="checkbox" id="mg-isEnforceInstructions" checked={formData.isEnforceInstructions ?? false} onChange={(e) => onChange({ ...formData, isEnforceInstructions: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          <label htmlFor="mg-isEnforceInstructions" className="ml-2 text-sm text-gray-700">Enforce Instructions</label>
        </div>
        <div className="flex items-center">
          <input type="checkbox" id="mg-isOnlyManager" checked={formData.isOnlyManager ?? false} onChange={(e) => onChange({ ...formData, isOnlyManager: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          <label htmlFor="mg-isOnlyManager" className="ml-2 text-sm text-gray-700">Only Manager</label>
        </div>
        <div className="flex items-center">
          <input type="checkbox" id="mg-isStaffMeal" checked={formData.isStaffMeal ?? false} onChange={(e) => onChange({ ...formData, isStaffMeal: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          <label htmlFor="mg-isStaffMeal" className="ml-2 text-sm text-gray-700">Staff Meal</label>
        </div>
        <div className="flex items-center">
          <input type="checkbox" id="mg-isShowNotification" checked={formData.isShowNotification ?? false} onChange={(e) => onChange({ ...formData, isShowNotification: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          <label htmlFor="mg-isShowNotification" className="ml-2 text-sm text-gray-700">Show Notification</label>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
        <input type="number" value={formData.sortOrder ?? ''} onChange={(e) => onChange({ ...formData, sortOrder: parseInt(e.target.value) || 0 })} className="input w-full" />
      </div>
      <ColorPickerField label="Button Color" value={formData.buttonColorCode} onChange={(v) => onChange({ ...formData, buttonColorCode: v })} />
      <ColorPickerField label="Text Color" value={formData.textColorCode} onChange={(v) => onChange({ ...formData, textColorCode: v })} />
      <TranslationsEditor restaurantId={restaurantId} translations={formData.translations || []} onChange={(translations) => onChange({ ...formData, translations })} />
    </div>
  );
}
